import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Loading from '../components/Loading';
import { useGetMatkaAgentReportQuery } from '../services/matka/matkaApi';

const colorVal = (v) => {
  const n = Number(v ?? 0);
  return <span className={n >= 0 ? 'text-green-600' : 'text-red-500'}>{n.toFixed(2)}</span>;
};

const COLS = [
  { label: 'Matka Amt.',   key: 'matkaAmt'    },
  { label: 'Total',        key: 'total'       },
  { label: 'Matka Comm',   key: 'matkaComm'   },
  { label: 'Total Comm',   key: 'totalComm'   },
  { label: 'Total Amount', key: 'totalAmount' },
  { label: 'My Share',     key: 'myShare'     },
  { label: 'Net Amount',   key: 'netAmount'   },
];

const MatkaAgentReportPage = () => {
  const { eventId } = useParams();
  const navigate    = useNavigate();
  const location    = useLocation();

  const userId   = new URLSearchParams(location.search).get('userId') || undefined;
  const userName = location.state?.userName || userId;

  const { data, isLoading, isFetching, error, refetch } = useGetMatkaAgentReportQuery(
    { eventId, userId },
    { skip: !eventId }
  );

  const rows    = data?.data    || [];
  const summary = data?.summary || {};

  const handleRowClick = (row) => {
    if (!row.userId) return;
    navigate(`/matka/agent-report/${eventId}?userId=${row.userId}`, {
      state: { userName: row.name || row.userId },
    });
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="bg-[#0F2327] px-5 py-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-white font-semibold text-base">
              {userId ? `${userName} — Downline Report` : 'Agent Client Report'}
            </div>
            <div className="text-gray-400 text-sm mt-0.5">{eventId}</div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={refetch}
              disabled={isFetching}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-1.5 text-sm transition-colors disabled:opacity-60"
            >
              {isFetching ? 'Loading...' : 'Refresh'}
            </button>
            {userId && (
              <button onClick={() => navigate(`/matka/agent-report/${eventId}`)} className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-1.5 text-sm transition-colors">
                Back to All
              </button>
            )}
            <button onClick={() => navigate(-1)} className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-1.5 text-sm transition-colors">
              Back
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="m-4 p-3 bg-red-100 text-red-700 border border-red-300 text-sm">
          {error?.data?.message || 'Failed to load report'}
        </div>
      )}

      <div className="overflow-x-auto">
        {isLoading || isFetching ? (
          <div className="flex justify-center py-16"><Loading /></div>
        ) : (
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-[#0F2327] text-white">
                <th className="px-4 py-3 text-left font-semibold border border-[#1a3a40]">Username</th>
                {COLS.map(c => (
                  <th key={c.key} className="px-4 py-3 text-left font-semibold border border-[#1a3a40]">{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-gray-400 border border-gray-200">
                    No data found
                  </td>
                </tr>
              ) : (
                <>
                  {rows.map((row, idx) => (
                    <tr key={row.userId || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-2.5 border border-gray-200 font-medium text-gray-800">
                        <div
                          className={row.userType === 'Client' ? 'inline-block' : 'text-blue-600 hover:underline cursor-pointer inline-block'}
                          onClick={() => row.userType !== 'Client' && handleRowClick(row)}
                        >
                          {row.name || '-'}
                        </div>
                        {row.code && <div className="text-gray-400 text-xs">{row.code}</div>}
                      </td>
                      {COLS.map(c => (
                        <td key={c.key} className="px-4 py-2.5 border border-gray-200">
                          {colorVal(row[c.key])}
                        </td>
                      ))}
                    </tr>
                  ))}

                  {/* Summary Row */}
                  <tr className="bg-gray-100 font-semibold">
                    <td className="px-4 py-2.5 border border-gray-200 text-gray-800">Total</td>
                    {COLS.map(c => (
                      <td key={c.key} className="px-4 py-2.5 border border-gray-200">
                        {colorVal(summary[c.key])}
                      </td>
                    ))}
                  </tr>
                </>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default MatkaAgentReportPage;
